// The Spark section of an event's Creative tab. Read-only, except that a
// producer or pilot answers their own waiting review here.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../components/ui/Alert';
import { Avatar } from '../components/ui/Avatar';
import { buttonClass } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { IconArrowUpRight } from '../components/ui/icons';
import { Panel } from '../components/ui/Panel';
import { Progress } from '../components/ui/Progress';
import { Skeleton } from '../components/ui/Skeleton';
import Reveal from '../components/ui/Reveal';
import { Tag } from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import { Tooltip } from '../components/ui/Tooltip';
import useUserStore from '../stores/useUserStore';
import { formatEventDate } from '../utils/date-utils';
import { getByEvent } from './sparkApi';
import type { CreativeTabProject } from './sparkApi';
import { RUNWAYS } from './sparkRules';
import { ReviewRequestCard, ReviewStatusText, StageTag, StatusTag, TaskStatusTag } from './SparkBits';

const TEAM_LABEL = { producer: 'Producer', designer: 'Designer', pilot: 'Pilot' } as const;

const Heading = ({ children }: { children: React.ReactNode }) => (
  <h4 className="m-0 mb-2 text-sm font-semibold text-on-surface">{children}</h4>
);

function PyroCheck({ lines, booked }: { lines: { effect: string; count: number }[]; booked: number | null }) {
  const planned = lines.reduce((sum, l) => sum + l.count, 0);
  if (lines.length === 0 && !booked) return null;
  const matches = booked !== null && planned === booked;
  return (
    <div>
      <Heading>Pyro plan</Heading>
      {lines.length > 0 && (
        <dl className="m-0 mb-2 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-sm">
          {lines.map((l) => (
            <React.Fragment key={l.effect}>
              <dt className="truncate text-on-surface">{l.effect}</dt>
              <dd className="text-right font-mono tabular-nums text-on-surface">{l.count}</dd>
            </React.Fragment>
          ))}
          <dt className="text-on-surface-variant">Planned</dt>
          <dd className="text-right font-mono font-semibold tabular-nums text-on-surface">{planned}</dd>
        </dl>
      )}
      <Alert tone={matches ? 'success' : 'warning'} live={matches ? false : 'alert'}>
        {matches
          ? `Matches the ${booked} pyro drones booked.`
          : booked === null
            ? `The plan adds up to ${planned}, but no pyro drones are booked on this event. Ask the Studio and the producer which is right.`
            : `The plan adds up to ${planned}, but ${booked} pyro drones are booked. Ask the Studio to correct the plan, or the producer to correct the booking.`}
      </Alert>
    </div>
  );
}

function Body({ project, userId, canOpen, onDone }: { project: CreativeTabProject; userId: string; canOpen: boolean; onDone: () => void }) {
  const { production: p, team, tasks, reviews, pyro } = project;
  const runway = RUNWAYS.find((r) => r.key === p.runway);
  const mine = reviews.filter((r) => r.status === 'waiting' && r.reviewer_id && String(r.reviewer_id) === userId);
  const deliverables = tasks.filter((t) => t.parent_id === null);
  const finished = deliverables.filter((t) => t.status === 'ready' || t.status === 'closed').length;
  const draft = team.some((m) => m.user_id && !m.approved_at);

  return (
    <Panel
      title={p.name}
      actions={canOpen ? (
        <Tooltip label="Open this project in Spark">
          <Link to={`/spark/projects/${p.id}`} className={buttonClass('tonal')}>
            Open in Spark
            <IconArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </Tooltip>
      ) : undefined}
      className="mb-4"
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2 text-sm text-on-surface-variant">
          <StageTag stage={p.stage} />
          <StatusTag row={p} />
          {p.review.state !== 'not_sent' && <ReviewStatusText state={p.review.state} />}
          {p.preview_due && (
            <span className="tabular-nums">
              Preview due {formatEventDate(p.preview_due)}
              {runway ? `, ${runway.label} runway` : ''}
            </span>
          )}
        </div>

        {mine.length > 0 && (
          <div className="space-y-3">
            {mine.map((r) => (
              <ReviewRequestCard
                key={r.id}
                review={{
                  id: r.id,
                  kind: r.kind,
                  step: r.step,
                  merged: r.merged,
                  ask: r.ask,
                  task_name: tasks.find((t) => t.id === r.task_id)?.name ?? 'A deliverable',
                  production_name: p.name,
                  production_id: p.id,
                }}
                onDone={onDone}
              />
            ))}
          </div>
        )}

        <div className="grid gap-5 md:grid-cols-2">
          <div className="min-w-0">
            <Heading>Deliverables</Heading>
            {deliverables.length === 0 ? (
              <EmptyState variant="line">No deliverables yet. The Studio adds them in Spark.</EmptyState>
            ) : (
              <>
                <Progress value={finished} max={deliverables.length} label="Deliverables ready" display="count" className="mb-3" />
                <ul className="m-0 list-none space-y-1.5 p-0">
                  {deliverables.map((t) => (
                    <li key={t.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-panel bg-surface-container px-3 py-2">
                      <span className="min-w-0 flex-1 basis-32">
                        <span className="block truncate text-sm font-medium text-on-surface">{t.name}</span>
                        <span className="text-xs tabular-nums text-on-surface-variant">
                          {t.due_date ? `Due ${formatEventDate(t.due_date)}` : 'No due date'}
                        </span>
                      </span>
                      <TaskStatusTag status={t.status} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="min-w-0 space-y-5">
            <div>
              <Heading>Show team</Heading>
              <ul className="m-0 list-none space-y-1.5 p-0">
                {team.map((m) => (
                  <li key={m.role} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-panel bg-surface-container px-3 py-2 text-sm">
                    <Avatar name={m.name} userId={m.user_id} color={m.avatar_color} shape={m.avatar_shape} accessory={m.avatar_accessory} tone={m.approved_at ? 'solid' : 'draft'} ring="ring-surface-container" decorative />
                    <span className="min-w-0 flex-1 basis-32">
                      <span className="block text-xs text-on-surface-variant">{TEAM_LABEL[m.role]}</span>
                      <span className="block text-on-surface">{m.name ?? <span className="text-on-surface-variant">Not drawn yet</span>}</span>
                    </span>
                    {m.on_crew && <Tag hex={CATEGORY.neutral.ink.hex}>Also on crew</Tag>}
                  </li>
                ))}
              </ul>
              <Reveal show={draft}>
                <p className="m-0 mt-2 text-xs text-on-surface-variant">Draft, not approved yet. The team can still change.</p>
              </Reveal>
            </div>
            <PyroCheck lines={pyro} booked={project.pyro_booked} />
          </div>
        </div>
      </div>
    </Panel>
  );
}

const SparkCreativePanel: React.FC<{ eventId: string }> = ({ eventId }) => {
  const user = useUserStore((s) => s.user);
  const [project, setProject] = useState<CreativeTabProject | null | undefined>(undefined);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      setProject((await getByEvent(eventId)).project);
      setError(false);
    } catch {
      setError(true);
    }
  }, [eventId]);

  useEffect(() => {
    setProject(undefined);
    load();
  }, [load]);

  const canOpen = !!(user?.isadmin || user?.can_admin || user?.can_animator || user?.can_producer || user?.can_pilot);

  if (error && project === undefined) {
    return (
      <Alert tone="error" className="mb-4" onRetry={load}>The Spark project did not load. Try again.</Alert>
    );
  }

  if (project === undefined) {
    return (
      <div role="status" aria-label="Loading the Spark project" className="mb-4 space-y-3 rounded-panel bg-surface-container-low p-4">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-4 w-80 max-w-full" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (project === null) {
    return <EmptyState variant="line" className="mb-4">No Spark project is linked to this show yet.</EmptyState>;
  }

  return <Body project={project} userId={String(user?.id ?? '')} canOpen={canOpen} onDone={load} />;
};

export default SparkCreativePanel;
